import clsx from 'clsx';
import { GridSpaceDataType, LevelType } from 'data/types';
import { AllAdjacentSpaces } from 'utils/grid';
import {
  getBottomWallPositionClass,
  getCornerPatchPositionClass,
  getHorizontalWallStyle,
  getLeftWallPositionClass,
  getOpenFloorPlanWallFillClass,
  getOpenFloorPlanWallFillStyle,
  getRightWallPositionClass,
  getTopWallPositionClass,
  getVerticalWallStyle,
  OpenFloorPlanWallAppearance,
  wallTouchesEnd,
  wallTouchesStart
} from 'utils/open-floor-plan-wall-appearance';
import {
  getMergedHorizontalBorderOffsets,
  getMergedVerticalBorderOffsets
} from 'utils/open-floor-plan-walls';
import { isOpenTo } from 'utils/open-to';
import { findSpaceById } from 'utils/structure';

type OpenFloorPlanWallsProps = {
  space: GridSpaceDataType;
  adjacentSpaces: AllAdjacentSpaces;
  structure: LevelType[];
  hasSectionColor: boolean;
  overlay?: boolean;
  hideOpenToWalls?: boolean;
  enlarged?: boolean;
  conditioned?: boolean;
};

function OpenFloorPlanWalls({
  space,
  adjacentSpaces,
  structure,
  hasSectionColor,
  overlay = false,
  hideOpenToWalls = true,
  enlarged = false,
  conditioned = true
}: OpenFloorPlanWallsProps) {
  const currentSpace = findSpaceById(structure, space.spaceId);
  const appearance: OpenFloorPlanWallAppearance = {
    overlay,
    hasSectionColor,
    sectionColorBg: space.sectionColor?.bg,
    conditioned,
    enlarged
  };
  const fillClass = getOpenFloorPlanWallFillClass(appearance);
  const fillStyle = getOpenFloorPlanWallFillStyle(appearance);

  const hiddenTopSpaces = hideOpenToWalls
    ? adjacentSpaces.top.filter((adjacentSpace) =>
        currentSpace ? isOpenTo(currentSpace, adjacentSpace.spaceId) : false
      )
    : [];
  const hiddenBottomSpaces = hideOpenToWalls
    ? adjacentSpaces.bottom.filter((adjacentSpace) =>
        currentSpace ? isOpenTo(currentSpace, adjacentSpace.spaceId) : false
      )
    : [];
  const hiddenLeftSpaces = hideOpenToWalls
    ? adjacentSpaces.left.filter((adjacentSpace) =>
        currentSpace ? isOpenTo(currentSpace, adjacentSpace.spaceId) : false
      )
    : [];
  const hiddenRightSpaces = hideOpenToWalls
    ? adjacentSpaces.right.filter((adjacentSpace) =>
        currentSpace ? isOpenTo(currentSpace, adjacentSpace.spaceId) : false
      )
    : [];

  const topWallOffsets = getMergedHorizontalBorderOffsets(
    space.position,
    hiddenTopSpaces.map((spaceItem) => spaceItem.position)
  );
  const bottomWallOffsets = getMergedHorizontalBorderOffsets(
    space.position,
    hiddenBottomSpaces.map((spaceItem) => spaceItem.position)
  );
  const leftWallOffsets = getMergedVerticalBorderOffsets(
    space.position,
    hiddenLeftSpaces.map((spaceItem) => spaceItem.position)
  );
  const rightWallOffsets = getMergedVerticalBorderOffsets(
    space.position,
    hiddenRightSpaces.map((spaceItem) => spaceItem.position)
  );

  const hasTopWallHidden = hiddenTopSpaces.length > 0;
  const hasBottomWallHidden = hiddenBottomSpaces.length > 0;
  const hasLeftWallHidden = hiddenLeftSpaces.length > 0;
  const hasRightWallHidden = hiddenRightSpaces.length > 0;
  const touchesTopLeftCorner =
    topWallOffsets.some((offsets) => wallTouchesStart(offsets, 'left')) &&
    leftWallOffsets.some((offsets) => wallTouchesStart(offsets, 'top'));
  const touchesTopRightCorner =
    topWallOffsets.some((offsets) => wallTouchesEnd(offsets, 'right')) &&
    rightWallOffsets.some((offsets) => wallTouchesStart(offsets, 'top'));
  const touchesBottomRightCorner =
    bottomWallOffsets.some((offsets) => wallTouchesEnd(offsets, 'right')) &&
    rightWallOffsets.some((offsets) => wallTouchesEnd(offsets, 'bottom'));
  const touchesBottomLeftCorner =
    bottomWallOffsets.some((offsets) => wallTouchesStart(offsets, 'left')) &&
    leftWallOffsets.some((offsets) => wallTouchesEnd(offsets, 'bottom'));

  return (
    <>
      {topWallOffsets.map((offsets) => (
        <div
          key={`top-wall-${space.id}-${offsets.left}-${offsets.right}`}
          className={clsx(
            'absolute left-0 right-0 z-[15]',
            getTopWallPositionClass(enlarged),
            fillClass
          )}
          style={getHorizontalWallStyle(offsets, appearance)}
        />
      ))}
      {bottomWallOffsets.map((offsets) => (
        <div
          key={`bottom-wall-${space.id}-${offsets.left}-${offsets.right}`}
          className={clsx(
            'absolute left-0 right-0 z-[15]',
            getBottomWallPositionClass(enlarged),
            fillClass
          )}
          style={getHorizontalWallStyle(offsets, appearance)}
        />
      ))}
      {leftWallOffsets.map((offsets) => (
        <div
          key={`left-wall-${space.id}-${offsets.top}-${offsets.bottom}`}
          className={clsx(
            'absolute bottom-0 top-0 z-[15]',
            getLeftWallPositionClass(enlarged),
            fillClass
          )}
          style={getVerticalWallStyle(offsets, appearance)}
        />
      ))}
      {rightWallOffsets.map((offsets) => (
        <div
          key={`right-wall-${space.id}-${offsets.top}-${offsets.bottom}`}
          className={clsx(
            'absolute bottom-0 top-0 z-[15]',
            getRightWallPositionClass(enlarged),
            fillClass
          )}
          style={getVerticalWallStyle(offsets, appearance)}
        />
      ))}
      {hasTopWallHidden && hasRightWallHidden && touchesTopRightCorner && (
        <div
          className={clsx(
            'absolute z-[15]',
            getCornerPatchPositionClass('top-right', enlarged),
            fillClass
          )}
          style={fillStyle}
        />
      )}
      {hasTopWallHidden && hasLeftWallHidden && touchesTopLeftCorner && (
        <div
          className={clsx(
            'absolute z-[15]',
            getCornerPatchPositionClass('top-left', enlarged),
            fillClass
          )}
          style={fillStyle}
        />
      )}
      {hasBottomWallHidden &&
        hasRightWallHidden &&
        touchesBottomRightCorner && (
          <div
            className={clsx(
              'absolute z-[15]',
              getCornerPatchPositionClass('bottom-right', enlarged),
              fillClass
            )}
            style={fillStyle}
          />
        )}
      {hasBottomWallHidden && hasLeftWallHidden && touchesBottomLeftCorner && (
        <div
          className={clsx(
            'absolute z-[15]',
            getCornerPatchPositionClass('bottom-left', enlarged),
            fillClass
          )}
          style={fillStyle}
        />
      )}
    </>
  );
}

export default OpenFloorPlanWalls;
